/**
 * Product Page Image Visibility Component
 * 
 * Handles showing/hiding product images on the product page based on selected variant.
 * This works independently from product-card logic and only activates when:
 * - Images are NOT inside a product-card element
 * - A product form is present (main product page)
 * 
 * Uses CSS-based visibility similar to product-card, but applies to the entire page
 * based on the selected variant's option values from the product form.
 */

(function() {
  'use strict';

  let styleElement = null;
  let currentVariantId = null;
  let variantSelector = null;

  /**
   * Gets the selected variant ID from available sources
   * @returns {string|null} The selected variant ID or null
   */
  function getSelectedVariantId() {
    // Priority 1: Variant Selector (Standard Form)
    if (variantSelector) {
        return variantSelector.value;
    }

    // Priority 2: Product Options Component (if exposes ID directly - currently usually exposes options)
    // We can try to match options to ID if needed, but often selector is sync'd
    const productForm = document.getElementById('main-product-form') || 
                        document.querySelector('form[action*="/cart/add"]');
    
    if (productForm) {
        const idInput = productForm.querySelector('[name="id"]');
        if (idInput) return idInput.value;
    }

    // Priority 3: URL Param (Initial Load)
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) return variantId;

    // Fallback: Default to first available variant ID from json data if present?
    // For now, return null to allow default behavior
    return null;
  }

  /**
   * Updates visibility of images based on selected variant ID
   * @param {string} variantId - The variant ID to match
   */
  function updateImageVisibility(variantId) {
    // Find all images with data-variant-ids
    // Note: We target images directly now as they carry the attribute
    // We also support containers having it
    const imagesWithIds = document.querySelectorAll('[data-variant-ids]');

    // Filter out those inside product-cards (handled by other component)
    const pageImages = Array.from(imagesWithIds).filter(el => !el.closest('product-card'));

    if (pageImages.length === 0) return;

    let matchedCount = 0;

    pageImages.forEach(img => {
      const validIds = (img.dataset.variantIds || '').split(',');
      const shouldShow = validIds.includes(variantId);

      if (shouldShow) {
        matchedCount++;
        img.style.setProperty('display', 'block', 'important');

        // Also ensure parent wrapper is visible if it was hidden
        if (img.parentElement.tagName === 'A' || img.parentElement.classList.contains('min-w-full')) {
           img.parentElement.style.removeProperty('display');
        }
      } else {
        img.style.setProperty('display', 'none', 'important');
      }
    });

    // Reset scroll position of parent scrollable-group-track to start
    requestAnimationFrame(() => {
      resetScrollableGroupTrack(pageImages);
    });

    currentVariantId = variantId;
  }

  /**
   * Resets the scroll position of parent scrollable-group-track elements to (0, 0)
   * @param {Array} pageImages - Array of image elements
   */
  function resetScrollableGroupTrack(pageImages) {
    // Find unique scrollable-group-track parents
    const scrollableTracks = new Set();
    pageImages.forEach(img => {
      const track = img.closest('scrollable-group-track');
      if (track) {
        scrollableTracks.add(track);
      }
    });

    // Reset each track's scroll position instantly (no smoothing)
    scrollableTracks.forEach(track => {
      track.style.overflow = 'hidden';
      track.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
      requestAnimationFrame(() => {
        track.style.removeProperty('overflow');
      });
    });
  }

  /**
   * Handles variant change events
   * @param {Event} event - The change event (optional)
   */
  /**
   * Handles variant change events
   * @param {Event} event - The change event (optional)
   */
  function handleVariantChange(event) {
    let newVariantId = null;

    // Get variant ID from product-options:variant-changed event
    // This event contains the correct variant ID (including related variants)
    if (event && event.detail) {
        // Use variantId directly from detail (preferred - may be related variant)
        if (event.detail.variantId) {
            newVariantId = event.detail.variantId.toString();
        } else if (event.detail.variant && event.detail.variant.id) {
            newVariantId = event.detail.variant.id.toString();
        }
    }

    if (!newVariantId) return;

    if (newVariantId && newVariantId !== currentVariantId) {
        updateImageVisibility(newVariantId);
        triggerEmblaReinit();
    }
  }
  
  /**
   * Triggers Embla carousel reinitialization
   */
  function triggerEmblaReinit() {
    try {
      const emblaCarousel = document.querySelector('embla-carousel');
      if (emblaCarousel && typeof emblaCarousel.initializeEmbla === 'function') {
        setTimeout(() => {
          try {
            emblaCarousel.initializeEmbla();
          } catch (error) {
            // Silently handle carousel errors
          }
        }, 50); // Slightly longer delay to ensure DOM is ready
      }
    } catch (error) {
      // Silently handle carousel not available
    }
  }

  /**
   * Initializes the component
   */
  function init() {
    // Check for images with data-variant-ids (excluding product cards)
    const imageContainers = document.querySelectorAll('[data-variant-ids]');
    const productPageImages = Array.from(imageContainers).filter(el => {
      return !el.closest('product-card');
    });

    if (productPageImages.length === 0) {
      // No product page images found, nothing to do
      return;
    }

    // Check for product form (main product page)
    const productForm = document.getElementById('main-product-form') || 
                       document.querySelector('form[action*="/cart/add"]');
    
    if (productForm) {
      // Find variant selector
      variantSelector = productForm.querySelector('select[name="id"]');
      
      // Listen for product-options:variant-changed event (PRIMARY - has correct variant ID including related variants)
      // This event is dispatched by component-product-options.js AFTER selecting the correct tier
      document.addEventListener('product-options:variant-changed', handleVariantChange);

      // Also listen for URL changes (popstate) for browser back/forward
      window.addEventListener('popstate', () => {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        if (variantId) {
          updateImageVisibility(variantId);
        }
      });
    }

    // Initial Load Logic
    const initialId = getSelectedVariantId();
    if (initialId) {
        updateImageVisibility(initialId);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

